import { AccordionSection, Flex, InputNumber, InputRange, Span } from "@goodfill/ui-components";
import { PricedBond } from "../../../PricedBond";
import { DateTime } from "luxon";
import { useCallback, useState } from "react";
import { BondFilter, BondFilterChipData, BondFilterData, BondFilterProps } from "../BondFilter";
import { BondFilterSectionGap, BondFilterSectionMargin } from "../BondFilterConst";
import { BondFilterCriteria } from "../BondFilterCriteria";
import { BondFilterDataConstraints } from "../BondFilterDataConstraints";
import {
  getSessionStorageNumber,
  removeItemFromSessionStorage,
  saveNumberToSessionStorage,
} from "../../../../../sessionStorage";
import { calculateDiffYears } from "@goodfill/date";

const FILTER_LOW_STORAGE_KEY = "filter:maturityYearsLowerBound";
const FILTER_HIGH_STORAGE_KEY = "filter:maturityYearsHigherBound";

const _Component = (props: BondFilterProps) => {
  const maturityMin = props.constraints.maturityMin;
  const maturityMax = props.constraints.maturityMax;
  const maturityStep = 1;
  const maturityLowerBound = props.criteria.maturityYearsLowerBound;
  const maturityHigherBound = props.criteria.maturityYearsHigherBound;
  const onRangeChange = useCallback(
    (low: number, hight: number) => {
      props.onChange({
        criteria: {
          ...props.criteria,
          maturityYearsLowerBound: low,
          maturityYearsHigherBound: hight,
        },
      });
    },
    [props],
  );

  const [maturitySectionIsOpen, setMaturitySectionIsOpen] = useState(true);

  const onChangeLeft = useCallback(
    (low: number) => onRangeChange(low, maturityHigherBound),
    [onRangeChange, maturityHigherBound],
  );
  const onChangeRight = useCallback(
    (high: number) => onRangeChange(maturityLowerBound, high),
    [onRangeChange, maturityLowerBound],
  );

  return (
    <AccordionSection
      title={
        <>
          Maturity<Span size="xs">&nbsp;(years)</Span>
        </>
      }
      isOpen={maturitySectionIsOpen}
      setIsOpen={setMaturitySectionIsOpen}
    >
      <Flex direction="col" verticalMargin={BondFilterSectionMargin} gap={BondFilterSectionGap}>
        <Flex direction="row" justify="between" className="px-2">
          <InputNumber
            border={"neutral"}
            width={"w-11"}
            min={maturityMin}
            max={maturityHigherBound - maturityStep}
            step={maturityStep}
            value={maturityLowerBound}
            setValue={(value) => {
              if (value === null) return;
              onChangeLeft(value);
            }}
            transparent={true}
            textAlign="center"
          />

          <InputNumber
            border={"neutral"}
            width={"w-11"}
            min={maturityLowerBound + maturityStep}
            max={maturityMax}
            step={maturityStep}
            value={maturityHigherBound}
            setValue={(value) => {
              if (value === null) return;
              onChangeRight(value);
            }}
            transparent={true}
            textAlign="center"
          />
        </Flex>

        <InputRange
          min={maturityMin}
          max={maturityMax}
          step={maturityStep}
          leftBound={maturityLowerBound}
          rightBound={maturityHigherBound}
          setLeftBound={onChangeLeft}
          setRightBound={onChangeRight}
        />
      </Flex>
    </AccordionSection>
  );
};

export class BondFilterMaturity implements BondFilter {
  isVisibleInDropdown(): boolean {
    return true;
  }

  reset(criteria: BondFilterCriteria, defaultCriteria: BondFilterCriteria): BondFilterCriteria {
    return {
      ...criteria,
      maturityYearsLowerBound: defaultCriteria.maturityYearsLowerBound,
      maturityYearsHigherBound: defaultCriteria.maturityYearsHigherBound,
    };
  }

  getChips(criteria: BondFilterCriteria, defaultCriteria: BondFilterCriteria): BondFilterChipData[] {
    const isActive =
      criteria.maturityYearsLowerBound !== defaultCriteria.maturityYearsLowerBound ||
      criteria.maturityYearsHigherBound !== defaultCriteria.maturityYearsHigherBound;

    return isActive
      ? [
          {
            id: "maturity",
            label: "Maturity",
            onClick: () => this.reset(criteria, defaultCriteria),
          },
        ]
      : [];
  }

  evaluate(data: BondFilterData, criteria: BondFilterCriteria): boolean {
    const now = DateTime.local();
    const maturityAbsHigh = now.plus({
      years: criteria.maturityYearsHigherBound,
    });
    const maturityAbsLow = now.plus({
      years: criteria.maturityYearsLowerBound,
    });

    return data.pricedBond.maturityDate <= maturityAbsHigh && data.pricedBond.maturityDate >= maturityAbsLow;
  }

  updateConstraintByPricedBond(pricedBond: PricedBond, current: BondFilterDataConstraints): BondFilterDataConstraints {
    const now = DateTime.local();

    const result = { ...current };

    const yearsDiff = calculateDiffYears(pricedBond.maturityDate, now);
    const yearsDiffLow = Math.floor(yearsDiff);
    const yearsDiffHigh = Math.ceil(yearsDiff);

    if (yearsDiffHigh > result.maturityMax) {
      result.maturityMax = yearsDiffHigh;
    }
    if (yearsDiffLow < result.maturityMin) {
      result.maturityMin = yearsDiffLow;
    }

    return result;
  }

  getDefaultCriteriaConstraints(pricedBond?: PricedBond): Partial<BondFilterDataConstraints> {
    if (pricedBond == null) {
      return { maturityMin: 0, maturityMax: 0 };
    }

    const now = DateTime.local();

    const diffYears = calculateDiffYears(pricedBond.maturityDate, now);

    return {
      maturityMin: Math.floor(diffYears),
      maturityMax: Math.ceil(diffYears),
    };
  }

  afterCriteriaCalculation(current: BondFilterDataConstraints): BondFilterDataConstraints {
    return current;
  }

  component = _Component;

  applyCriteriaFromStorage(criteria: Partial<BondFilterCriteria>): Partial<BondFilterCriteria> {
    return {
      ...criteria,
      maturityYearsLowerBound: getSessionStorageNumber(FILTER_LOW_STORAGE_KEY),
      maturityYearsHigherBound: getSessionStorageNumber(FILTER_HIGH_STORAGE_KEY),
    };
  }

  storeCriteriaToStorage(criteria: BondFilterCriteria, defaultCriteria: BondFilterCriteria): void {
    if (criteria.maturityYearsLowerBound === defaultCriteria.maturityYearsLowerBound) {
      removeItemFromSessionStorage(FILTER_LOW_STORAGE_KEY);
    } else {
      saveNumberToSessionStorage(FILTER_LOW_STORAGE_KEY, criteria.maturityYearsLowerBound);
    }

    if (criteria.maturityYearsHigherBound === defaultCriteria.maturityYearsHigherBound) {
      removeItemFromSessionStorage(FILTER_HIGH_STORAGE_KEY);
    } else {
      saveNumberToSessionStorage(FILTER_HIGH_STORAGE_KEY, criteria.maturityYearsHigherBound);
    }
  }
}
